import {
  backgroundOf,
  classifyLocation,
  isBaseRoute,
  isStackRoute,
  type BaseRouteClass,
  type LocationLike,
  type StackRouteClass,
} from './routes';

// A Flutter-style navigator stack kept in sync with browser history.
//
// The shell (tab stack or overlay) is the root route. Pushed pages and modal
// layers are stack entries above it, keyed by their history entry. Pushing
// appends, Back truncates to the entry it lands on (earlier entries stay
// mounted, like routes under a Flutter route), and REPLACE swaps the top.
// `location.state.background` is only read to rebuild what lies underneath
// when the stack has to be reconstructed (cold start, reload, or Back onto an
// entry this session never rendered).

export type NavDirection = 'forward' | 'back' | 'none';

/** A router Location with every field present (react-router's `Location` fits). */
export interface NavLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
}

export interface StackEntry {
  key: string;
  location: NavLocation;
  route: StackRouteClass;
}

export interface NavigatorState {
  locationKey: string;
  historyIdx: number | null;
  signedIn: boolean;
  /** The last tab/overlay location: what the shell shows underneath the stack. */
  base: NavLocation;
  baseRoute: BaseRouteClass;
  entries: StackEntry[];
  /** How the latest change moved through the stack (drives page transitions). */
  direction: NavDirection;
}

export interface NavigatorInput {
  location: NavLocation;
  navigationType: 'POP' | 'PUSH' | 'REPLACE';
  /** `history.state.idx` written by react-router (null when unknown). */
  historyIdx: number | null;
  signedIn: boolean;
  dev?: boolean;
}

export const HOME_LOCATION: NavLocation = { pathname: '/', search: '', hash: '', state: null, key: 'default' };
const HOME_ROUTE: BaseRouteClass = { kind: 'tab', tab: 'home' };
const MAX_CHAIN_DEPTH = 4;

function toNavLocation(like: LocationLike, fallbackKey: string): NavLocation {
  return {
    pathname: like.pathname,
    search: like.search ?? '',
    hash: like.hash ?? '',
    state: like.state ?? null,
    key: like.key || fallbackKey,
  };
}

interface ChainResult {
  base: { location: NavLocation; route: BaseRouteClass } | null;
  entries: StackEntry[];
}

/** Expands `state.background` links into the base and the stack entries below `location`. */
export function expandBackgroundChain(location: NavLocation, opts: { signedIn: boolean; dev?: boolean }): ChainResult {
  const chain: NavLocation[] = [];
  let cursor: LocationLike | null = backgroundOf(location);
  while (cursor && chain.length < MAX_CHAIN_DEPTH) {
    chain.unshift(toNavLocation(cursor, `bg${chain.length}:${cursor.pathname}`));
    cursor = backgroundOf(cursor);
  }
  let base: ChainResult['base'] = null;
  const entries: StackEntry[] = [];
  for (const loc of chain) {
    const route = classifyLocation(loc, opts);
    if (isBaseRoute(route)) {
      base = { location: loc, route };
      entries.length = 0;
    } else if (isStackRoute(route)) {
      entries.push({ key: loc.key, location: loc, route });
    }
  }
  return { base, entries };
}

/** Same history entry (keeps object identity so memoised layers don't re-render). */
function sameEntry(a: NavLocation, b: NavLocation): boolean {
  return a.key === b.key && a.pathname === b.pathname && a.search === b.search;
}

function isBackward(prevIdx: number | null | undefined, idx: number | null): boolean {
  return prevIdx != null && idx != null && idx < prevIdx;
}

/** Computes the navigator state after a location change. Pure. */
export function reduceNavigator(prev: NavigatorState | null, input: NavigatorInput): NavigatorState {
  const { location, navigationType, historyIdx, signedIn, dev } = input;
  const route = classifyLocation(location, { signedIn, dev });
  const prevEntries = prev?.entries ?? [];
  const common = { locationKey: location.key, historyIdx, signedIn };
  let base = prev?.base ?? HOME_LOCATION;
  let baseRoute = prev?.baseRoute ?? HOME_ROUTE;

  if (!isStackRoute(route)) {
    if (isBaseRoute(route)) {
      if (!prev || !sameEntry(base, location)) base = location;
      baseRoute = route;
    }
    return { ...common, base, baseRoute, entries: [], direction: prevEntries.length > 0 ? 'back' : 'none' };
  }

  const known = prevEntries.findIndex((e) => e.key === location.key);
  if (known >= 0) {
    const truncated = known < prevEntries.length - 1;
    return {
      ...common,
      base,
      baseRoute,
      entries: truncated ? prevEntries.slice(0, known + 1) : prevEntries,
      direction: truncated ? 'back' : prev?.direction ?? 'none',
    };
  }

  const entry: StackEntry = { key: location.key, location, route };
  const back = navigationType === 'POP' && isBackward(prev?.historyIdx, historyIdx);

  if (prevEntries.length === 0 || back) {
    const chain = expandBackgroundChain(location, { signedIn, dev });
    if (chain.base && (!prev || !sameEntry(base, chain.base.location))) {
      base = chain.base.location;
      baseRoute = chain.base.route;
    }
    return {
      ...common,
      base,
      baseRoute,
      entries: [...chain.entries, entry],
      direction: back ? 'back' : prev ? 'forward' : 'none',
    };
  }

  if (navigationType === 'REPLACE') {
    return { ...common, base, baseRoute, entries: [...prevEntries.slice(0, -1), entry], direction: 'forward' };
  }
  return { ...common, base, baseRoute, entries: [...prevEntries, entry], direction: 'forward' };
}

/** Whether the reducer needs to run for this render. */
export function needsReduce(state: NavigatorState, location: NavLocation, signedIn: boolean): boolean {
  return state.locationKey !== location.key || state.signedIn !== signedIn;
}
